import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  FlatList,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  RefreshControl,
  TextInput,
  Image,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../../components/common/ThemedText';
import { getAllPayments, ClubPayment } from '../../lib/api/payments';
import { showErrorToast } from '../../utils/toast';

type AdminPaymentsNavigationProp = StackNavigationProp<RootStackParamList>;

const AdminPaymentsScreen: React.FC = () => {
  const { theme } = useTheme();
  const navigation = useNavigation<AdminPaymentsNavigationProp>();

  const [payments, setPayments] = useState<ClubPayment[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [search, setSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadPayments = useCallback(async (searchQuery = '', refreshing = false) => {
    try {
      if (refreshing) setIsRefreshing(true);
      else if (!searchQuery) setIsLoading(true);

      const data = await getAllPayments(searchQuery ? { search: searchQuery } : undefined);
      setPayments(data);
    } catch (error: any) {
      showErrorToast(error.message || 'Failed to load payments');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const handleSearchChange = (text: string) => {
    setSearch(text);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadPayments(text);
    }, 350);
  };

  const handleUserPress = (userId: string) => {
    navigation.navigate('Profile', { userId });
  };

  const getStatusColor = (status: ClubPayment['status']) => {
    switch (status) {
      case 'paid': return '#10B981';
      case 'pending': return '#F59E0B';
      case 'failed': return theme.error;
      case 'refunded': return theme.textSecondary;
      default: return theme.textSecondary;
    }
  };

  const getStatusIcon = (status: ClubPayment['status']): keyof typeof Ionicons['glyphMap'] => {
    switch (status) {
      case 'paid': return 'checkmark-circle';
      case 'pending': return 'time';
      case 'failed': return 'close-circle';
      case 'refunded': return 'refresh-circle';
      default: return 'help-circle';
    }
  };

  const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const totalPaid = payments
    .filter((p) => p.status === 'paid')
    .reduce((sum, p) => sum + p.amount, 0);

  const renderPayment = ({ item }: { item: ClubPayment }) => (
    <TouchableOpacity
      style={[styles.paymentCard, { backgroundColor: theme.card, borderColor: theme.border }]}
      onPress={() => item.user?.id && handleUserPress(item.user.id)}
      activeOpacity={item.user?.id ? 0.7 : 1}
    >
      {/* User Avatar */}
      <View style={styles.avatarContainer}>
        {item.user?.photo ? (
          <Image source={{ uri: item.user.photo }} style={styles.avatar} />
        ) : (
          <View style={[styles.avatarFallback, { backgroundColor: theme.primary + '25' }]}>
            <ThemedText style={[styles.avatarInitial, { color: theme.primary }]}>
              {(item.user?.name || 'U').charAt(0).toUpperCase()}
            </ThemedText>
          </View>
        )}
      </View>

      {/* Details */}
      <View style={styles.cardBody}>
        <View style={styles.cardTopRow}>
          <ThemedText style={[styles.userName, { color: theme.text }]} numberOfLines={1}>
            {item.user?.name || 'Unknown User'}
          </ThemedText>
          {item.user?.id && (
            <Ionicons name="chevron-forward" size={14} color={theme.textTertiary} />
          )}
        </View>
        {item.user?.profession && (
          <ThemedText style={[styles.professionText, { color: theme.textSecondary }]} numberOfLines={1}>
            {item.user.profession}
          </ThemedText>
        )}
        <View style={styles.clubRow}>
          <Ionicons name="people-outline" size={12} color={theme.textTertiary} />
          <ThemedText style={[styles.clubNameText, { color: theme.textTertiary }]} numberOfLines={1}>
            {item.clubName}
          </ThemedText>
        </View>
        <ThemedText style={[styles.dateText, { color: theme.textSecondary }]}>
          {item.paidAt ? formatDate(item.paidAt) : formatDate(item.createdAt)}
        </ThemedText>
        {item.razorpayPaymentId && (
          <ThemedText style={[styles.txnId, { color: theme.textTertiary }]} numberOfLines={1}>
            {item.razorpayPaymentId}
          </ThemedText>
        )}
      </View>

      {/* Amount + Status */}
      <View style={styles.cardRight}>
        <ThemedText style={[styles.amountText, { color: theme.text }]}>
          ₹{item.amount.toLocaleString('en-IN')}
        </ThemedText>
        <View style={[styles.statusBadge, { backgroundColor: getStatusColor(item.status) + '20' }]}>
          <Ionicons name={getStatusIcon(item.status)} size={12} color={getStatusColor(item.status)} />
          <ThemedText style={[styles.statusText, { color: getStatusColor(item.status) }]}>
            {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
          </ThemedText>
        </View>
      </View>
    </TouchableOpacity>
  );

  const renderEmpty = () => (
    <View style={styles.emptyContainer}>
      <Ionicons name="receipt-outline" size={56} color={theme.textTertiary} />
      <ThemedText style={[styles.emptyTitle, { color: theme.text }]}>
        {search ? 'No results found' : 'No payments yet'}
      </ThemedText>
      <ThemedText style={[styles.emptySubtext, { color: theme.textSecondary }]}>
        {search ? `No payments matching "${search}"` : 'Club membership payments will appear here.'}
      </ThemedText>
    </View>
  );

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: theme.background }]} edges={['top', 'left', 'right']}>
      {/* Header */}
      <View style={[styles.header, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <TouchableOpacity onPress={() => navigation.goBack()} activeOpacity={0.7}>
          <Ionicons name="arrow-back" size={24} color={theme.text} />
        </TouchableOpacity>
        <ThemedText style={[styles.headerTitle, { color: theme.text }]}>All Payments</ThemedText>
        <View style={{ width: 24 }} />
      </View>

      {/* Search Bar */}
      <View style={[styles.searchBar, { backgroundColor: theme.backgroundSecondary, borderBottomColor: theme.border }]}>
        <View style={[styles.searchInputRow, { backgroundColor: theme.card, borderColor: theme.border }]}>
          <Ionicons name="search" size={18} color={theme.textSecondary} />
          <TextInput
            style={[styles.searchInput, { color: theme.text }]}
            placeholder="Search by name, club or txn ID…"
            placeholderTextColor={theme.textTertiary}
            value={search}
            onChangeText={handleSearchChange}
            returnKeyType="search"
            autoCorrect={false}
          />
          {search.length > 0 && (
            <TouchableOpacity onPress={() => handleSearchChange('')} activeOpacity={0.7}>
              <Ionicons name="close-circle" size={18} color={theme.textSecondary} />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.primary} />
        </View>
      ) : (
        <FlatList
          data={payments}
          keyExtractor={(item) => item.id}
          renderItem={renderPayment}
          ListEmptyComponent={renderEmpty}
          contentContainerStyle={[styles.listContent, payments.length === 0 && styles.listContentEmpty]}
          refreshControl={
            <RefreshControl
              refreshing={isRefreshing}
              onRefresh={() => loadPayments(search, true)}
              tintColor={theme.primary}
            />
          }
          ListHeaderComponent={
            payments.length > 0 ? (
              <View style={[styles.summaryRow, { backgroundColor: theme.primary + '18', borderColor: theme.primary + '40' }]}>
                <View style={styles.summaryItem}>
                  <ThemedText style={[styles.summaryLabel, { color: theme.textSecondary }]}>Total Collected</ThemedText>
                  <ThemedText style={[styles.summaryValue, { color: theme.primary }]}>
                    ₹{totalPaid.toLocaleString('en-IN')}
                  </ThemedText>
                </View>
                <View style={[styles.summaryDivider, { backgroundColor: theme.primary + '30' }]} />
                <View style={styles.summaryItem}>
                  <ThemedText style={[styles.summaryLabel, { color: theme.textSecondary }]}>Transactions</ThemedText>
                  <ThemedText style={[styles.summaryValue, { color: theme.text }]}>{payments.length}</ThemedText>
                </View>
                <View style={[styles.summaryDivider, { backgroundColor: theme.primary + '30' }]} />
                <View style={styles.summaryItem}>
                  <ThemedText style={[styles.summaryLabel, { color: theme.textSecondary }]}>Paid</ThemedText>
                  <ThemedText style={[styles.summaryValue, { color: '#10B981' }]}>
                    {payments.filter((p) => p.status === 'paid').length}
                  </ThemedText>
                </View>
              </View>
            ) : null
          }
        />
      )}
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  searchBar: {
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderBottomWidth: 1,
  },
  searchInputRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 12,
    paddingVertical: 9,
  },
  searchInput: {
    flex: 1,
    fontSize: 14,
    padding: 0,
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  listContent: {
    padding: 16,
    gap: 10,
  },
  listContentEmpty: {
    flex: 1,
  },
  summaryRow: {
    flexDirection: 'row',
    borderRadius: 12,
    borderWidth: 1,
    padding: 14,
    marginBottom: 8,
  },
  summaryItem: {
    flex: 1,
    alignItems: 'center',
    gap: 3,
  },
  summaryLabel: {
    fontSize: 11,
    textAlign: 'center',
  },
  summaryValue: {
    fontSize: 15,
    fontWeight: '700',
  },
  summaryDivider: {
    width: 1,
    marginHorizontal: 8,
  },
  paymentCard: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
    gap: 10,
  },
  avatarContainer: {
    flexShrink: 0,
  },
  avatar: {
    width: 44,
    height: 44,
    borderRadius: 22,
  },
  avatarFallback: {
    width: 44,
    height: 44,
    borderRadius: 22,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitial: {
    fontSize: 18,
    fontWeight: '700',
  },
  cardBody: {
    flex: 1,
    gap: 2,
  },
  cardTopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  userName: {
    flex: 1,
    fontSize: 15,
    fontWeight: '600',
  },
  professionText: {
    fontSize: 12,
  },
  clubRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    marginTop: 2,
  },
  clubNameText: {
    fontSize: 12,
    flex: 1,
  },
  dateText: {
    fontSize: 12,
  },
  txnId: {
    fontSize: 11,
  },
  cardRight: {
    alignItems: 'flex-end',
    gap: 6,
    flexShrink: 0,
  },
  amountText: {
    fontSize: 15,
    fontWeight: '700',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 8,
  },
  statusText: {
    fontSize: 11,
    fontWeight: '600',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 32,
    gap: 12,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
  },
  emptySubtext: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default AdminPaymentsScreen;
